/**
 * Profiles Service — single source of truth for `profiles` table queries.
 *
 * Schema notes:
 *   - `user_type` is the canonical IDENTITY bucket: 'internal' | 'external'
 *     (see src/types/userType.ts). Job-function info lives in `profile_roles`,
 *     not here. Use `profilesService.isStaff()` as the single canonical
 *     "is this person a CIWG employee?" predicate.
 *   - Permission checks: use CASL `ability.can(action, subject)` from
 *     AbilityContext — NOT the legacy is_* flag columns.
 *   - Legacy is_* flag columns still exist as identity-capability flags
 *     for routing/dropdowns (sales_rep, account_manager, designer,
 *     helpdesk_agent). They are kept in sync from profile_roles by trigger.
 */
import { supabase } from "@/integrations/supabase/client";
import { isStaffUserType, type UserType } from "@/types/userType";

export type { UserType };

export interface ProfileRow {
  id?: string | null;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  cell_phone: string | null;
  company_name: string | null;
  title: string | null;
  website: string | null;
  avatar_url: string | null;
  company_logo_url: string | null;
  user_type: UserType | null;
  booking_link: string | null;
  is_sales_rep: boolean | null;
  is_account_manager: boolean | null;
  is_designer: boolean | null;
  is_helpdesk_agent: boolean | null;
  is_marketing_local: boolean | null;
  is_marketing_seo: boolean | null;
  is_marketing_pulse: boolean | null;
  is_super_admin: boolean | null;
  is_billing: boolean | null;
  is_performance_team: boolean | null;
  is_hr: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  [k: string]: unknown;
}

export interface ProfileFilters {
  userType?: UserType | UserType[];
  email?: string;
}

const DEFAULT_COLUMNS = "*";

function applyFilters(q: any, filters: ProfileFilters = {}): any {
  if (filters.userType) {
    q = Array.isArray(filters.userType)
      ? q.in("user_type", filters.userType)
      : q.eq("user_type", filters.userType);
  }
  if (filters.email) q = q.ilike("email", filters.email);
  return q;
}

export const profilesService = {
  /** Find profile by auth user_id. Null if none. */
  async findByUserId(userId: string, columns: string = DEFAULT_COLUMNS): Promise<ProfileRow | null> {
    if (!userId) return null;
    const { data, error } = await (supabase as any)
      .from("profiles")
      .select(columns)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw error;
    return (data ?? null) as ProfileRow | null;
  },

  /** Find profile by case-insensitive email. */
  async findByEmail(email: string, columns: string = DEFAULT_COLUMNS): Promise<ProfileRow | null> {
    if (!email) return null;
    const { data, error } = await (supabase as any)
      .from("profiles")
      .select(columns)
      .ilike("email", email)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return (data ?? null) as ProfileRow | null;
  },

  /** Find profile by case-insensitive full_name (used by helpdesk recipient lookup). */
  async findByFullName(name: string, columns: string = DEFAULT_COLUMNS): Promise<ProfileRow | null> {
    if (!name) return null;
    const { data, error } = await (supabase as any)
      .from("profiles")
      .select(columns)
      .ilike("full_name", name)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return (data ?? null) as ProfileRow | null;
  },

  /** List profiles with optional filters. */
  async list(columns: string = DEFAULT_COLUMNS, filters: ProfileFilters = {}): Promise<ProfileRow[]> {
    let q = (supabase as any).from("profiles").select(columns);
    q = applyFilters(q, filters);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as ProfileRow[];
  },

  /** List internal staff (CIWG employees). */
  async listInternal(columns: string = "user_id, full_name, email, title"): Promise<ProfileRow[]> {
    return this.list(columns, { userType: "internal" });
  },

  /**
   * Canonical "is this profile a CIWG employee?" predicate.
   * Every UI/RLS check should funnel through here (or `isStaffUserType`
   * from src/types/userType) — never re-derive with email-domain or flag
   * checks.
   */
  isStaff(profile: { user_type?: string | null } | null | undefined): boolean {
    return isStaffUserType(profile?.user_type ?? null);
  },

  /** Get display name for a user_id (full_name → email → "Unknown"). */
  async getDisplayName(userId: string): Promise<string> {
    const p = await this.findByUserId(userId, "full_name, email");
    return p?.full_name || p?.email || "Unknown";
  },


  /** Update arbitrary profile fields by user_id (partial). */
  async update(userId: string, patch: Partial<ProfileRow>): Promise<void> {
    const { error } = await (supabase as any)
      .from("profiles")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("user_id", userId);
    if (error) throw error;
  },
};

export type ProfilesService = typeof profilesService;
